/**
 * Carbon side panel: a headless UI Dialog anchored to the right edge, on
 * layer-01 with a 1px border-strong leading edge over a 60% black scrim, and
 * no shadow (Layer-Not-Shadow Rule). It slides in over 240ms. A pinned header
 * (title, subtitle, Close), a body that scrolls under it, and an optional
 * pinned footer of actions. md 480px, lg 640px, full screen below c-md. For
 * URL-driven drawers (`?drawer=`), derive `open` from the URL and clear it in
 * `onClose`.
 *
 * Testing: like Modal, the dialog portals after mount; SSR-test the content
 * through SidePanelFrame.
 */
import { Fragment, type ElementType, type MutableRefObject, type ReactNode } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { IconButton } from './IconButton'
import { IconClose } from './icons'

export interface SidePanelFrameProps {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  /** Footer actions (Buttons), pinned under the body and sharing its width. */
  actions?: ReactNode
  titleAs?: ElementType
  children?: ReactNode
}

export interface SidePanelProps extends Omit<SidePanelFrameProps, 'titleAs'> {
  open: boolean
  size?: 'md' | 'lg'
  initialFocus?: MutableRefObject<HTMLElement | null>
}

/** The panel's content without the dialog shell: header, body and footer. */
export function SidePanelFrame({ title, subtitle, onClose, actions, titleAs: Title = 'h2', children }: SidePanelFrameProps) {
  return (
    <>
      <header className="relative flex-none border-b border-line-subtle py-4 pl-4 pr-14">
        <Title className="text-heading font-normal text-text-primary">{title}</Title>
        {subtitle ? <p className="mt-1 text-body text-text-secondary">{subtitle}</p> : null}
        <IconButton label="Close" icon={<IconClose />} onClick={onClose} className="absolute right-0 top-0" />
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 pt-4 text-body text-text-primary">{children}</div>
      {actions ? <footer className="flex flex-none gap-px [&>*]:max-w-none [&>*]:flex-1">{actions}</footer> : null}
    </>
  )
}

export function SidePanel({ open, onClose, size = 'md', initialFocus, ...frame }: SidePanelProps) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[60]" onClose={onClose} initialFocus={initialFocus}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-150 ease-carbon"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-110 ease-carbon"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div aria-hidden="true" className="fixed inset-0 bg-black/60" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="transition-transform duration-240 ease-carbon-in"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition-transform duration-150 ease-carbon"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <Dialog.Panel
            className={clsx(
              'fixed inset-y-0 right-0 flex w-full flex-col border-l border-line-strong bg-layer-1 text-text-primary max-c-md:border-l-0',
              size === 'lg' ? 'c-md:max-w-[640px]' : 'c-md:max-w-[480px]',
            )}
          >
            <SidePanelFrame {...frame} onClose={onClose} titleAs={Dialog.Title} />
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  )
}

export default SidePanel
